import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { FormBuilder, FormGroup, Validators, ValidationErrors } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import * as moment from 'moment';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ClientCatalogItem, ClientCatalogService } from 'src/app/services/client-catalog.service';
import { SnackBarService } from 'src/app/services/snack-bar.service';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { environment } from 'src/environments/environment';

interface ClientLiquidationOrder {
  id: string;
  pedido: string;
  fecha: string;
  cadete: string;
  totalCalculado: number;
  totalFinal: number;
  valorConIVA: number | null;
  comision: number;
  comisionPorcentaje: number;
  comisionPorDefecto: boolean;
  servicio: string;
  esValida?: boolean;
  motivoInvalidez?: string | null;
}

interface ClientLiquidationResponse {
  cdClient: number;
  listIds: string[];
  orders: ClientLiquidationOrder[];
}

@Component({
  selector: 'app-liquidaciones-clientes',
  templateUrl: './liquidaciones-clientes.component.html',
  styleUrls: ['./liquidaciones-clientes.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class LiquidacionesClientesComponent implements OnInit, AfterViewInit, OnDestroy {

  displayedColumns: string[] = ['select', 'id', 'fecha', 'cadete', 'totalCalculado', 'totalFinal', 'valorConIVA', 'comision', 'servicio'];
  selection = new SelectionModel<ClientLiquidationOrder>(true, []);
  dataSource = new MatTableDataSource<ClientLiquidationOrder>([]);
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  allClients: ClientCatalogItem[] = [];
  selectedClient: ClientCatalogItem | null = null;
  private readonly clientServices = ['Mensajeria', 'Punto Fijo'];
  private destroy$ = new Subject<void>();
  searchSeq = 0;

  dateFrom: any;
  dateTo: any;
  totalLiquidacion = 0;
  totalComision = 0;
  totalLiq = 0;
  totalRecibos = 0;
  observaciones = '';
  numeroLiquidacion = '';
  password = '';
  isSubmitting = false;
  hasDefaultCommission = false;

  private clientSelectedValidator = (): ValidationErrors | null =>
    this.selectedClient ? null : { required: true };

  clientForm: FormGroup = this.fb.group({
    client: ['', Validators.required],
    clientSearch: ['', this.clientSelectedValidator],
  });

  private selectableOrders(): ClientLiquidationOrder[] {
    return this.dataSource.data.filter(order => order.esValida !== false);
  }

  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.selectableOrders().length;
    return numSelected === numRows && numRows > 0;
  }

  masterToggle(): void {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.selectableOrders().forEach(row => this.selection.select(row));
    }
    this.recalculateTotals();
  }

  toggle(row: ClientLiquidationOrder): void {
    if (row.esValida === false) {
      return;
    }
    this.selection.toggle(row);
    this.recalculateTotals();
  }

  private recalculateTotals(): void {
    this.totalLiquidacion = 0;
    this.totalComision = 0;
    this.totalLiq = 0;

    this.totalRecibos = this.selection.selected.length;

    this.selection.selected.forEach((order: ClientLiquidationOrder) => {
      this.totalLiquidacion += order.totalFinal ?? 0;
      this.totalComision += order.comision ?? 0;
    });

    this.totalLiq = this.totalLiquidacion - this.totalComision;
    this.totalLiquidacion = Number(this.totalLiquidacion.toFixed(2));
    this.totalComision = Number(this.totalComision.toFixed(2));
    this.totalLiq = Number(this.totalLiq.toFixed(2));
  }

  constructor(
    private authService: AuthService,
    private fb: FormBuilder,
    private snackBar: SnackBarService,
    private http: HttpClient,
    private dialog: MatDialog,
    private clientCatalog: ClientCatalogService,
  ) {
  }

  ngOnInit(): void {
    this.clientForm.get('clientSearch')?.valueChanges.pipe(
      // La carga inicial entra por el mismo stream: así una búsqueda más nueva
      // cancela la respuesta anterior aunque llegue fuera de orden.
      startWith(''),
      tap(value => {
        if (typeof value === 'string') {
          this.onClientTextChanged(value);
        }
      }),
      debounceTime(300),
      distinctUntilChanged(),
      // switchMap cancela la búsqueda anterior: no llegan respuestas fuera de orden.
      switchMap(value => this.clientCatalog.search(this.clientServices, typeof value === 'string' ? value : '')),
      takeUntil(this.destroy$)
    ).subscribe({
      next: response => this.allClients = response.data,
      error: error => this.handleClientsError(error)
    });
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  ngOnDestroy(): void {
    this.searchSeq++;
    this.destroy$.next();
    this.destroy$.complete();
  }

  displayClient(value: ClientCatalogItem | string | null): string {
    if (!value || typeof value === 'string') {
      return value || '';
    }
    return value.dsNombreFantasia || value.dsRazonSocial || '';
  }

  onClientSelected(client: ClientCatalogItem): void {
    this.selectedClient = client;
    this.clientForm.get('client')?.setValue(client.cdClient);
    this.clientForm.get('clientSearch')?.updateValueAndValidity({ emitEvent: false });
    this.clearData();
  }

  private onClientTextChanged(value: string): void {
    if (this.selectedClient) {
      const selectedName = this.displayClient(this.selectedClient);
      if (value !== selectedName) {
        this.selectedClient = null;
        this.clientForm.get('client')?.setValue('');
        this.clientForm.get('clientSearch')?.updateValueAndValidity({ emitEvent: false });
        this.clearData();
      }
    }
  }

  private handleClientsError(error: any): void {
    this.allClients = [];
    const message = typeof error.error === 'string'
      ? error.error
      : error.error?.error || 'No se pudieron cargar los clientes.';
    this.snackBar.openSnackBar(message, 'Aceptar');
  }

  private clearData(): void {
    this.searchSeq++;
    this.dataSource.data = [];
    this.selection.clear();
    this.totalLiquidacion = 0;
    this.totalComision = 0;
    this.totalLiq = 0;
    this.totalRecibos = 0;
    this.hasDefaultCommission = false;
    this.paginator?.firstPage();
  }

  hasDefaultCommissionRows(): boolean {
    return this.hasDefaultCommission;
  }

  inpuDateFrom(event: any): void {
    this.dateFrom = event.value;
    this.clearData();
  }

  inpuDateTo(event: any): void {
    this.dateTo = event.value;
    this.clearData();
  }

  search(): void {
    if (!this.clientForm.get('client')?.value) {
      this.clientForm.markAllAsTouched();
      this.snackBar.openSnackBar('Debe seleccionar un cliente.', 'Aceptar');
      return;
    }

    this.clearData();
    const seq = this.searchSeq;

    const dateFrom = this.dateFrom ? moment(this.dateFrom).startOf('day').toDate() : undefined;
    const dateTo = this.dateTo ? moment(this.dateTo).endOf('day').toDate() : undefined;

    const calculateUrl = `${environment.apiBaseUrl}/api/ClientLiquidation/CalculateLiquidation`;
    const params: any = { cdClient: this.clientForm.get('client')?.value };
    if (dateFrom) {
      params.dtFrom = dateFrom.toISOString();
    }
    if (dateTo) {
      params.dtTo = dateTo.toISOString();
    }

    this.http.get<ClientLiquidationResponse>(calculateUrl, { params }).subscribe({
      next: (data) => {
        if (seq !== this.searchSeq) {
          return;
        }
        const orders = data.orders || [];
        this.dataSource.data = orders;
        this.hasDefaultCommission = orders.some(order => order.esValida !== false && order.comisionPorDefecto);
        this.selection.clear();
        this.selectableOrders().forEach(order => this.selection.select(order));
        this.recalculateTotals();
        this.paginator?.firstPage();

        const invalidOrders = orders.filter(order => order.esValida === false);
        if (invalidOrders.length > 0) {
          const detail = invalidOrders
            .slice(0, 3)
            .map(order => `${order.pedido}: ${order.motivoInvalidez || 'sin detalle'}`)
            .join(' | ');
          this.snackBar.openSnackBar(
            `${invalidOrders.length} operación(es) no se pueden liquidar. ${detail}`,
            'Aceptar'
          );
        }
      },
      error: (error) => {
        if (seq !== this.searchSeq) {
          return;
        }
        const message = typeof error.error === 'string'
          ? error.error
          : error.error?.error || 'Error al calcular la liquidación';
        this.snackBar.openSnackBar(message, 'Aceptar');
      }
    });
  }

  confirmLiquidation(): void {
    const currentUser = this.authService.currentUser();
    const selectedIds = this.selection.selected.map(s => s.id);

    if (selectedIds.length === 0) {
      this.snackBar.openSnackBar('No hay operaciones seleccionadas. Por favor, selecciona al menos una para liquidar.', 'Aceptar');
      return;
    }

    if (!this.numeroLiquidacion || !this.numeroLiquidacion.trim()) {
      this.snackBar.openSnackBar('Debe ingresar el número de liquidación.', 'Aceptar');
      return;
    }

    if (this.numeroLiquidacion.trim().length > 50) {
      this.snackBar.openSnackBar('El número de liquidación no puede superar los 50 caracteres.', 'Aceptar');
      return;
    }

    if (!/^[A-Za-z0-9._\-\/ ]+$/.test(this.numeroLiquidacion.trim())) {
      this.snackBar.openSnackBar('El número de liquidación contiene caracteres inválidos.', 'Aceptar');
      return;
    }

    if (!this.password) {
      this.snackBar.openSnackBar('Debe ingresar la contraseña para emitir la liquidación.', 'Aceptar');
      return;
    }

    this.recalculateTotals();

    const defaultCommissionCount = this.selection.selected
      .filter(order => order.comisionPorDefecto)
      .length;

    let message = `¿Emitir la liquidación N° ${this.numeroLiquidacion.trim()} de ${this.totalRecibos} operación(es) `
      + `por un total de $${this.totalLiquidacion}, comisión $${this.totalComision} y neto $${this.totalLiq}?`;

    if (defaultCommissionCount > 0) {
      message += ` Atención: ${defaultCommissionCount} operación(es) usan la comisión por defecto porque no hay regla configurada ni comisión del cadete.`;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '520px',
      data: {
        message,
        confirmButtonText: 'Emitir',
        cancelButtonText: 'Cancelar',
      }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.emitLiquidation(selectedIds, currentUser);
      }
    });
  }

  private emitLiquidation(selectedIds: string[], currentUser: any): void {
    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;

    const liquidationPayload = {
      cdClient: this.clientForm.get('client')?.value,
      listIds: selectedIds,
      dsNumeroLiquidacion: this.numeroLiquidacion.trim(),
      dsObservation: this.observaciones,
      dsUsername: currentUser?.dsUsername,
      dsPassword: this.password,
    };

    const liquidateUrl = `${environment.apiBaseUrl}/api/ClientLiquidation/PostLiquidation`;
    this.http.post(liquidateUrl, liquidationPayload).subscribe({
      next: () => {
        this.snackBar.openSnackBar('Liquidación emitida con éxito.', 'Aceptar');
        this.clearData();
        this.observaciones = '';
        this.numeroLiquidacion = '';
        this.password = '';
        this.isSubmitting = false;
      },
      error: (error) => {
        const message = typeof error.error === 'string'
          ? error.error
          : error.error?.error || error.error?.errors?.[0] || 'Error al procesar la liquidación';
        this.snackBar.openSnackBar(message, 'Aceptar');
        this.isSubmitting = false;
      }
    });
  }
}
